/**
 * Intent Filter Config Hooks
 * Per-intent filtering toggles + account-wide enable/disable for intent-based filtering.
 */

import { useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { intentFilterConfigService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';

// The brand is in every settings key as well as in the request. Two brands must never
// read each other's cached settings, and switching brands no longer clears the cache —
// a key without the brand would serve the previous brand's values.

/** Account flag key holding the intent-filtering enable/disable state. */
export const INTENT_FILTERING_ENABLED_FLAG = 'intent_filtering_enabled';

export const enabledIntentFilterConfigsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['account-intent-filter-configs', 'enabled', buScopeKey(get)],
  queryFn: () => intentFilterConfigService.getEnabledConfigs(buScopeParams(get))
}));

export function useEnabledIntentFilterConfigs() {
  const [query] = useAtom(enabledIntentFilterConfigsQueryAtom);
  return query;
}

export const upsertIntentFilterConfigMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account-intent-filter-configs', 'upsert'],
  mutationFn: ({ intentId, isFilteringEnabled }: { intentId: string; isFilteringEnabled: boolean }) =>
    intentFilterConfigService.upsertConfig(intentId, isFilteringEnabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account-intent-filter-configs']);
  }
}));

export function useUpsertIntentFilterConfig() {
  const [mutation] = useAtom(upsertIntentFilterConfigMutationAtom);
  return mutation;
}

export const bulkIntentFilterConfigMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account-intent-filter-configs', 'bulk'],
  mutationFn: (enabled: boolean) => intentFilterConfigService.setFilteringForAll(enabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account-intent-filter-configs']);
  }
}));

export function useBulkIntentFilterConfig() {
  const [mutation] = useAtom(bulkIntentFilterConfigMutationAtom);
  return mutation;
}

export const bypassTaggingConfigsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['account-intent-filter-configs', 'bypass-tagging', buScopeKey(get)],
  queryFn: () => intentFilterConfigService.getBypassTaggingConfigs(buScopeParams(get))
}));

export function useBypassTaggingConfigs() {
  const [query] = useAtom(bypassTaggingConfigsQueryAtom);
  return query;
}

export const setBypassTaggingMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account-intent-filter-configs', 'bypass-tagging', 'update'],
  mutationFn: ({ intentId, bypassTagging }: { intentId: string; bypassTagging: boolean }) =>
    intentFilterConfigService.setBypassTagging(intentId, bypassTagging, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account-intent-filter-configs']);
  }
}));

export function useSetBypassTagging() {
  const [mutation] = useAtom(setBypassTaggingMutationAtom);
  return mutation;
}

export const updateIntentFilteringEnabledMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['account', 'intent-filtering-enabled'],
  mutationFn: (enabled: boolean) =>
    intentFilterConfigService.updateFilteringEnabled(enabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['account', 'flags']);
  }
}));

export function useUpdateIntentFilteringEnabled() {
  const [mutation] = useAtom(updateIntentFilteringEnabledMutationAtom);
  return mutation;
}
